// The label link slot (2026-10-26, docs/pipeline-behaviors.md "Return label
// on file: recover and send at Process"; docs/agent-draft-shadow.md "The
// label link").
//
// A return label on file is sent by its staged step, "recover and send"
// (actions.js's resend_return_label with params.recoverAndSend): it runs at
// Process, before the reply is sent, and the reply tells the customer the
// label has been sent, with its link. When the link was recovered while the
// suggestion was built, it is in the reply already. When it was not, the
// reply keeps the template's INSERTLABELLINK where the link goes, and the
// step's result fills it at Process (its slotUrls: the links it recovered
// then). A slot still unfilled at Send is refused like any INSERT...
// placeholder (findUnresolvedPlaceholders()).
//
// Pure: no calls. Used by the server (agent-draft-primary.js's reply check)
// and the panel (processAll.js, SuggestionPanel.jsx).

import { labelLinkBlocks } from './label-boxes.js'

export const LABEL_LINK_TOKEN = 'INSERTLABELLINK'

// The index of the staged step that fills the slot: a processable
// resend_return_label "recover and send" step with a label whose link is
// not known yet, or (2026-10-27) a non-eBay order's label create for an RMA
// with none (params.flow 'B-create' with no eBay message after it: its new
// label's link goes in the reply). -1 when there is none.
export function labelLinkFillStepIndex(actions = []) {
  const list = actions ?? []
  return list.findIndex((a) => a?.type === 'resend_return_label'
    && Boolean(a?.targetSystem)
    && ((a?.params?.recoverAndSend === true && labelsOf(a).some((l) => !l?.labelUrl))
      || (a?.params?.flow === 'B-create' && !a?.params?.labelUrl && !list.some((b) => b?.type === 'resend_return_label_send_message'))))
}

function labelsOf(a) {
  const p = a?.params ?? {}
  if (Array.isArray(p.labels) && p.labels.length) return p.labels
  return [{ trackingNumber: p.trackingNumber ?? null, labelUrl: p.labelUrl ?? null }]
}

// Whether a reply's INSERTLABELLINK is a slot a staged step still fills:
// the reply keeps the token, and the fill step is on the suggestion and has
// not run yet (stepStates: the panel's, by index; a step at 'success' has
// run).
export function hasPendingLabelLinkSlot(reply, actions = [], stepStates = {}) {
  if (!String(reply ?? '').includes(LABEL_LINK_TOKEN)) return false
  const i = labelLinkFillStepIndex(actions)
  return i >= 0 && stepStates?.[i]?.status !== 'success'
}

// The links a processed fill step's result puts in the slot: the ones it
// recovered at Process (slotUrls), else its one link.
export function labelLinkSlotUrls(result) {
  if (Array.isArray(result?.slotUrls) && result.slotUrls.length) return result.slotUrls.filter(Boolean)
  // A create's labels, one per package (2026-10-27): every box's label in
  // order (boxLabels: the labels on file first, then the new ones), else the
  // labels it created.
  const createdList = Array.isArray(result?.boxLabels) && result.boxLabels.length ? result.boxLabels : result?.labels
  const created = result?.recoverAndSend !== true && Array.isArray(createdList) ? createdList.filter((l) => l && !l.error && l.labelUrl).map((l) => l.labelUrl) : []
  if (created.length) return [...new Set(created)]
  return [result?.labelUrl].filter(Boolean)
}

// The reply with its slot filled: each link on its own line (an HTML reply
// gets <br>); boxes (2026-10-27, one return's boxes): one "Box 1 of 2:" block
// per link. Unchanged when there is no link or no slot.
export function fillLabelLinkSlot(reply, urls = [], { boxes = false } = {}) {
  const text = String(reply ?? '')
  const list = (urls ?? []).filter(Boolean)
  if (!list.length || !text.includes(LABEL_LINK_TOKEN)) return text
  const joiner = /<[a-z][^>]*>/i.test(text) ? '<br>' : '\n'
  return text.split(LABEL_LINK_TOKEN).join(boxes ? labelLinkBlocks(list, { joiner }) : list.join(joiner))
}
